import { useMemo } from 'react';
import * as yup from 'yup';
import { isValidPhoneNumber } from '../helpers/isValidPhoneNumber';
import { useTranslation } from '../../../i18n/translate/useTranslation';
import { UNITED_STATES } from '../../../constants/UnitedStates';
import { useSignUpSchema } from '../../../../auth/sign-up/ui/form/helpers/useSignUpSchema';
import { IDetailsFormData } from '../types/IDetailsFormData';

interface IOptions {
	requiresConsent?: boolean;
}

export function useDetailsSchema({ requiresConsent = true }: IOptions = {}) {
	const { fields: signUpSchema } = useSignUpSchema();
	const { t } = useTranslation();

	const schema = useMemo(
		() =>
			yup
				.object<IDetailsFormData>({
					firstName: signUpSchema.firstName,
					lastName: signUpSchema.lastName,
					email: signUpSchema.email,
					phoneNumber: yup
						.string()
						.required(t('phone_number_form_field_error'))
						.test(
							'is-valid-phone-number',
							withMessageWhenNotEmpty(
								t('parking_personal_details_phone_number_format_error'),
							),
							isValidPhoneNumber,
						),
					address: yup.string().required(t('address_form_field_error')),
					city: yup.string().required(t('city_form_field_error')),
					state: yup
						.string()
						.oneOf(UNITED_STATES.map((item) => item.name))
						.required(t('state_form_field_error')),
					zipCode: yup.string().required(t('zip_code_form_field_error')),
				})
				.required(),
		[signUpSchema, requiresConsent],
	);

	return schema;
}

function withMessageWhenNotEmpty(message: string) {
	return ({ originalValue }: { originalValue?: string }) =>
		// When no value was entered we don't provide an explicit error message
		originalValue ? message : '';
}
